import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import {
  listDuplicates,
  markDistinct,
  type DuplicateOrganisation,
  type DuplicatePair,
  type DuplicatePairs,
} from './api'
import { MergeOrganisationsDialog } from './MergeOrganisationsDialog'

function reasonText(pair: DuplicatePair): string {
  if (pair.reason === 'naam') return 'Zelfde naam'
  if (pair.reason === 'domein') return 'Zelfde domein'
  const score = pair.score === null ? '' : ` (${pair.score.toFixed(2).replace('.', ',')})`
  return `Lijkt op${score}`
}

function OrganisationSummary({ organisation }: { organisation: DuplicateOrganisation }) {
  const details = [organisation.domains[0] ?? organisation.website, organisation.city].filter(Boolean)
  const members = organisation.members_count === 1 ? '1 contactpersoon' : `${organisation.members_count} contactpersonen`
  return (
    <div className="min-w-0 flex-1">
      <p className="font-medium break-words">{organisation.name}</p>
      <p className="text-xs text-muted-foreground break-words">{[...details, members].join(' · ')}</p>
    </div>
  )
}

/** The pairs that look alike, each with "Samenvoegen…" and "Geen dubbel". */
export function DuplicatesDialog({
  open,
  onOpenChange,
  pairs,
  onChange,
  onMerged,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pairs: DuplicatePairs
  onChange: (pairs: DuplicatePairs) => void
  onMerged?: () => void
}) {
  const [merging, setMerging] = useState<DuplicatePair | null>(null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const keyOf = (pair: DuplicatePair) => `${pair.first.id}:${pair.second.id}`

  async function notDuplicate(pair: DuplicatePair) {
    setBusyKey(keyOf(pair))
    try {
      await markDistinct(pair.first.id, pair.second.id)
      const items = pairs.items.filter((item) => keyOf(item) !== keyOf(pair))
      onChange({ ...pairs, items, total: Math.max(0, pairs.total - 1) })
      toast.success('Gemarkeerd als geen dubbel')
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Markeren mislukt.')
    } finally {
      setBusyKey(null)
    }
  }

  async function merged() {
    setMerging(null)
    onMerged?.()
    try {
      onChange(await listDuplicates())
    } catch (caught) {
      // The list stays as it was; the merged pair disappears on the next load.
      toastFailure(caught, 'Lijst verversen mislukt.')
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Mogelijke dubbele organisaties</DialogTitle>
            <DialogDescription>Voeg samen wat dezelfde organisatie is; markeer de rest als geen dubbel.</DialogDescription>
          </DialogHeader>
          {pairs.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Geen mogelijke dubbelen meer.</p>
          ) : (
            <ul className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
              {pairs.items.map((pair) => (
                <li key={keyOf(pair)} className="flex flex-col gap-2 rounded-lg border border-border p-3">
                  <div className="flex flex-col gap-2 md:flex-row md:gap-4">
                    <OrganisationSummary organisation={pair.first} />
                    <OrganisationSummary organisation={pair.second} />
                  </div>
                  <p className="text-xs font-medium">{reasonText(pair)}</p>
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={busyKey === keyOf(pair)}
                      onClick={() => void notDuplicate(pair)}
                      aria-label={`Geen dubbel: ${pair.first.name} en ${pair.second.name}`}
                    >
                      Geen dubbel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setMerging(pair)}
                      aria-label={`Samenvoegen: ${pair.first.name} en ${pair.second.name}`}
                    >
                      Samenvoegen…
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
      {merging ? (
        <MergeOrganisationsDialog
          firstId={merging.first.id}
          secondId={merging.second.id}
          onClose={() => setMerging(null)}
          onMerged={() => void merged()}
        />
      ) : null}
    </>
  )
}
